import { type FC, useEffect, useId } from "react"

import type { LandingPageLinkOption, LandingPageLinkOptions } from "@forento/core/models/landing"
import type { LandingPageLink } from "@forento/core/utilities/landingLink"
import { cn } from "@forento/ui/utilities/tailwind"

import { query } from "~/utilities/trpc"

const linkTypeLabels: Record<LandingPageLink["type"], string> = {
	page: "Page on your website",
	academy: "Page on your academy",
	custom: "Custom link",
}

const controlClassName =
	"w-full min-w-0 rounded-lg border border-sky-200 bg-white px-3 py-2.5 text-gray-900 text-sm shadow-xs outline-none transition-colors placeholder:text-gray-400 focus:border-sky-400 focus:ring-2 focus:ring-sky-100"

const emptyLinkByType: Record<LandingPageLink["type"], LandingPageLink> = {
	page: { type: "page", path: "" },
	academy: { type: "academy", path: "" },
	custom: { type: "custom", url: "" },
}

function getHost(value: string): string | null {
	try {
		return new URL(value).host
	} catch {
		return null
	}
}

// BACKWARDS COMPAT: a button saved before typed links became an academy-hosted custom link, so it is shown
// as the academy page it points at and stored as one the next time the creator saves the block.
function toEditableLink(link: LandingPageLink, academyUrl: string | undefined): LandingPageLink {
	if (link.type !== "custom" || academyUrl === undefined) return link

	const academyHost = getHost(academyUrl)
	if (academyHost === null || getHost(link.url) !== academyHost) return link

	const { pathname, search, hash } = new URL(link.url)
	return { type: "academy", path: `${pathname}${search}${hash}`, openInNewTab: link.openInNewTab }
}

type Props = {
	value: LandingPageLink
	onChange(value: LandingPageLink): void
	className?: string
}
const ButtonLinkCard: FC<Props> = ({ value, onChange, className }) => {
	const typeInputId = useId()
	const targetInputId = useId()
	const { data: linkOptions, isLoading } = query.landing.linkOptions.useQuery(undefined, {
		refetchOnWindowFocus: false,
	})

	const link = toEditableLink(value, linkOptions?.academyUrl)
	// The upgrade is stored rather than only shown, so a legacy link stops depending on the domain it was
	// written with even when the creator never opens this picker again.
	useEffect(() => {
		if (link !== value) onChange(link)
	}, [link, value, onChange])

	const path = link.type === "custom" ? "" : link.path.trim()
	const options = getOptions(link.type, linkOptions)
	// Keeps a target the options no longer offer (an unpublished product, a deleted page) selected instead
	// of silently clearing the creator's link.
	const hasUnlistedTarget = path.length > 0 && !options.some(option => option.path === path)
	// Picking another target keeps how the link opens, since that is a separate choice from where it goes.
	const changeTarget = (target: LandingPageLink) => onChange({ ...target, openInNewTab: link.openInNewTab })

	return (
		<div
			className={cn(
				"rounded-2xl border border-sky-200 bg-sky-50/70 p-5 shadow-sm transition-colors focus-within:border-sky-400 focus-within:bg-white focus-within:ring-2 focus-within:ring-sky-100",
				className,
			)}
		>
			<label htmlFor={typeInputId} className="mb-2 block text-sm font-semibold text-sky-950">
				Button link
			</label>
			<select
				id={typeInputId}
				value={link.type}
				onChange={event => changeTarget(emptyLinkByType[event.target.value as LandingPageLink["type"]])}
				className={cn(controlClassName, "cursor-pointer")}
			>
				{Object.entries(linkTypeLabels).map(([type, label]) => (
					<option key={type} value={type}>
						{label}
					</option>
				))}
			</select>
			<div className="mt-2">
				{link.type === "custom" ? (
					<input
						id={targetInputId}
						type="url"
						value={link.url}
						onChange={event => changeTarget({ type: "custom", url: event.target.value })}
						placeholder="https://..."
						inputMode="url"
						spellCheck={false}
						aria-label="Button URL"
						className={controlClassName}
					/>
				) : (
					<>
						<select
							id={targetInputId}
							value={path}
							onChange={event => changeTarget({ type: link.type, path: event.target.value })}
							disabled={isLoading}
							aria-label={linkTypeLabels[link.type]}
							className={cn(controlClassName, "cursor-pointer disabled:cursor-default")}
						>
							<option value="">{isLoading ? "Loading pages..." : "Select a page"}</option>
							{hasUnlistedTarget && <option value={path}>{path}</option>}
							{link.type === "page"
								? linkOptions?.sitePages.map(option => (
										<option key={option.path} value={option.path}>
											{option.label}
										</option>
									))
								: linkOptions?.academyPages.map(group => (
										<optgroup key={group.label} label={group.label}>
											{group.options.map(option => (
												<option key={option.path} value={option.path}>
													{option.label}
												</option>
											))}
										</optgroup>
									))}
						</select>
						{path.length > 0 && (
							<p className="mt-2 text-xs break-all text-sky-950/60">
								{link.type === "academy" ? `${linkOptions?.academyUrl ?? ""}${path}` : path}
							</p>
						)}
					</>
				)}
			</div>
			<label className="mt-3 flex cursor-pointer items-center gap-2.5">
				<input
					type="checkbox"
					checked={link.openInNewTab ?? false}
					onChange={event => onChange({ ...link, openInNewTab: event.target.checked })}
					className="cursor-pointer rounded border-sky-300 text-sky-700"
				/>
				<span className="text-sm text-sky-950">Open in a new tab</span>
			</label>
		</div>
	)
}

function getOptions(
	type: LandingPageLink["type"],
	linkOptions: LandingPageLinkOptions | undefined,
): LandingPageLinkOption[] {
	if (linkOptions === undefined) return []
	return type === "page" ? linkOptions.sitePages : linkOptions.academyPages.flatMap(group => group.options)
}

export default ButtonLinkCard
